/**
 * 🔐 Auth Service
 * Authentifizierung und Benutzer-Management für React
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

// Axios interceptor für automatische Token-Behandlung
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('isendinbox_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('isendinbox_token');
      localStorage.removeItem('isendinbox_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('isendinbox_token');
      const savedUser = localStorage.getItem('isendinbox_user');
      
      if (token && savedUser) {
        setUser(JSON.parse(savedUser));
        
        // Verify token with server
        try {
          const response = await axios.get('/api/auth/me');
          if (response.data.success) {
            setUser(response.data.data);
          }
        } catch (error) {
          // Token invalid, clear storage
          localStorage.removeItem('isendinbox_token');
          localStorage.removeItem('isendinbox_user');
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        username,
        password
      });

      if (response.data.success) {
        const { token, user } = response.data.data;
        
        localStorage.setItem('isendinbox_token', token);
        localStorage.setItem('isendinbox_user', JSON.stringify(user));
        
        setUser(user);
        toast.success('Erfolgreich angemeldet!');
        
        return { success: true };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Login fehlgeschlagen';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('isendinbox_token');
    localStorage.removeItem('isendinbox_user');
    setUser(null);
    toast.success('Erfolgreich abgemeldet');
  };

  const register = async (username, email, password) => {
    try {
      const response = await axios.post('/api/auth/register', {
        username,
        email,
        password
      });

      if (response.data.success) {
        toast.success('Registrierung erfolgreich! Bitte melden Sie sich an.');
        return { success: true };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Registrierung fehlgeschlagen';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    register
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
